// src/scenes/SignUpSceneDOM.js - Full-page sign-up experience matching templates exactly
import BaseMenuScene from "./BaseMenuScene.js";
import { MobileDetectionService } from "../services/MobileDetectionService.js";

export default class SignUpSceneDOM extends BaseMenuScene {
    constructor() {
        super({ key: "SignUpSceneDOM" });
        this.overlayId = "signup-scene-overlay";
        this.currentStep = 1;
        this.signupData = {};
    }

    create() {
        console.log("SignUpSceneDOM: Creating full-page sign-up experience");
        super.create();
        
        // Hide game container and show full-page sign-up
        this.hideGameContainer();
        this.setRibbonVisibility(false); // Hide ribbon for clean sign-up experience
        
        // Create full-page sign-up interface
        this.createSignUpInterface();
    }

    hideGameContainer() {
        const gameContainer = document.getElementById("game-container");
        if (gameContainer) {
            this.originalGameContainerStyles = {
                display: gameContainer.style.display || 'block',
                visibility: gameContainer.style.visibility || 'visible'
            };
            gameContainer.style.display = 'none';
            gameContainer.style.visibility = 'hidden';
        }
    }

    showGameContainer() {
        const gameContainer = document.getElementById("game-container");
        if (gameContainer && this.originalGameContainerStyles) {
            gameContainer.style.display = this.originalGameContainerStyles.display;
            gameContainer.style.visibility = this.originalGameContainerStyles.visibility;
        }
    }

    createSignUpInterface() {
        // Remove existing overlay
        this.removeExistingOverlay();

        // Detect mobile and use appropriate template
        const isMobile = MobileDetectionService.isMobile();
        console.log(`SignUpSceneDOM: Device detection - Mobile: ${isMobile}`);
        
        if (isMobile) {
            this.createMobileSignUpInterface();
        } else {
            this.createDesktopSignUpInterface();
        }
    }

    createDesktopSignUpInterface() {
        // Create main overlay using DESKTOP template design
        const overlay = document.createElement('div');
        overlay.id = this.overlayId;
        overlay.style.cssText = `
            position: fixed;
            top: 0;
            left: 0;
            width: 100vw;
            height: 100vh;
            background: #0f172a;
            color: #ffffff;
            z-index: 10000;
            overflow-y: auto;
            display: flex;
            align-items: center;
            justify-content: center;
            padding: 40px 20px;
            font-family: -apple-system, BlinkMacSystemFont, 'SF Pro Display', 'Segoe UI', system-ui, sans-serif;
        `;

        overlay.innerHTML = `
            <!-- Premium Background -->
            <div class="background" style="
                position: fixed;
                inset: 0;
                background: 
                    radial-gradient(circle at 30% 20%, rgba(0, 212, 255, 0.08) 0%, transparent 50%),
                    radial-gradient(circle at 70% 60%, rgba(14, 165, 233, 0.06) 0%, transparent 50%),
                    radial-gradient(circle at 50% 50%, #1e293b 0%, #0f172a 100%);
                z-index: -2;
            "></div>

            <!-- Sign Up Card - Wide Layout (800px) -->
            <div class="signup-card" style="
                width: 100%;
                max-width: 800px;
                background: rgba(30, 41, 59, 0.5);
                backdrop-filter: blur(40px);
                -webkit-backdrop-filter: blur(40px);
                border: 1px solid rgba(255, 255, 255, 0.08);
                border-radius: 24px;
                overflow: hidden;
                box-shadow: 
                    0 20px 50px rgba(0, 0, 0, 0.5),
                    0 0 100px rgba(0, 212, 255, 0.1),
                    inset 0 1px 1px rgba(255, 255, 255, 0.05);
                animation: cardEntry 0.8s cubic-bezier(0.21, 1.02, 0.73, 1);
            ">
                <!-- Card Header -->
                <div class="card-header" style="
                    padding: 32px 48px;
                    background: linear-gradient(135deg, rgba(255, 255, 255, 0.02) 0%, rgba(255, 255, 255, 0.05) 100%);
                    border-bottom: 1px solid rgba(255, 255, 255, 0.08);
                ">
                    <div class="header-content" style="
                        display: flex;
                        align-items: center;
                        justify-content: space-between;
                    ">
                        <div class="logo-section" style="
                            display: flex;
                            align-items: center;
                            gap: 16px;
                        ">
                            <div class="logo" style="
                                width: 48px;
                                height: 48px;
                                background: linear-gradient(135deg, #00d4ff 0%, #0ea5e9 100%);
                                border-radius: 14px;
                                display: flex;
                                align-items: center;
                                justify-content: center;
                                font-size: 24px;
                                font-weight: 800;
                                color: #0f172a;
                                box-shadow: 0 8px 24px rgba(0, 212, 255, 0.3);
                            ">D</div>
                            <div class="brand-name" style="
                                font-size: 20px;
                                font-weight: 700;
                                letter-spacing: -0.5px;
                                color: #ffffff;
                            ">DeckOps.ai</div>
                        </div>
                        <button class="close-btn" id="close-signup" style="
                            width: 36px;
                            height: 36px;
                            background: rgba(255, 255, 255, 0.05);
                            border: 1px solid rgba(255, 255, 255, 0.08);
                            border-radius: 10px;
                            display: flex;
                            align-items: center;
                            justify-content: center;
                            color: rgba(148, 163, 184, 1);
                            cursor: pointer;
                            transition: all 0.15s ease;
                            font-size: 18px;
                        ">×</button>
                    </div>
                </div>

                <!-- Progress Bar -->
                <div class="signup-progress-bar">
                    <div class="signup-progress-steps">
                        <div class="signup-progress-line">
                            <div class="signup-progress-line-fill" id="progressFill" style="width: 25%"></div>
                        </div>
                        
                        <div class="signup-step active" id="step1">
                            <div class="signup-step-number"><span>1</span></div>
                            <div class="signup-step-label">Account</div>
                        </div>
                        
                        <div class="signup-step" id="step2">
                            <div class="signup-step-number"><span>2</span></div>
                            <div class="signup-step-label">Personal</div>
                        </div>
                        
                        <div class="signup-step" id="step3">
                            <div class="signup-step-number"><span>3</span></div>
                            <div class="signup-step-label">Professional</div>
                        </div>
                        
                        <div class="signup-step" id="step4">
                            <div class="signup-step-number"><span>4</span></div>
                            <div class="signup-step-label">Verification</div>
                        </div>
                    </div>
                </div>

                <!-- Card Body -->
                <div class="card-body" style="padding: 48px;">
                    <form id="signupForm">
                        <!-- Step 1: Account -->
                        <div class="form-step active" id="formStep1">
                            <h2 style="
                                font-size: 28px;
                                font-weight: 600;
                                letter-spacing: -0.5px;
                                margin-bottom: 8px;
                                color: #ffffff;
                            ">Create your executive account</h2>
                            <p style="
                                font-size: 14px;
                                color: rgba(255, 255, 255, 0.8);
                                margin-bottom: 32px;
                            ">Join thousands of Fortune 500 executives and consultants</p>
                            
                            <div class="form-group full-width" style="margin-bottom: 24px;">
                                <label style="
                                    display: block;
                                    font-size: 12px;
                                    font-weight: 600;
                                    color: rgba(255, 255, 255, 0.8);
                                    text-transform: uppercase;
                                    letter-spacing: 0.8px;
                                    margin-bottom: 8px;
                                ">Corporate Email <span style="color: #ef4444;">*</span></label>
                                <div class="input-wrapper" style="position: relative;">
                                    <svg class="input-icon" style="
                                        position: absolute;
                                        left: 16px;
                                        top: 50%;
                                        transform: translateY(-50%);
                                        width: 18px;
                                        height: 18px;
                                        color: rgba(148, 163, 184, 1);
                                    " fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
                                    </svg>
                                    <input type="email" id="corporateEmail" placeholder="john.doe@company.com" required style="
                                        width: 100%;
                                        height: 48px;
                                        padding: 0 16px 0 44px;
                                        background: rgba(255, 255, 255, 0.05);
                                        border: 1px solid rgba(255, 255, 255, 0.12);
                                        border-radius: 12px;
                                        color: #ffffff;
                                        font-size: 15px;
                                        transition: all 0.15s ease;
                                    ">
                                </div>
                            </div>
                            
                            <div class="form-row" style="
                                display: grid;
                                grid-template-columns: 1fr 1fr;
                                gap: 20px;
                            ">
                                <div class="form-group" style="margin-bottom: 24px;">
                                    <label style="
                                        display: block;
                                        font-size: 12px;
                                        font-weight: 600;
                                        color: rgba(255, 255, 255, 0.8);
                                        text-transform: uppercase;
                                        letter-spacing: 0.8px;
                                        margin-bottom: 8px;
                                    ">First Name <span style="color: #ef4444;">*</span></label>
                                    <input type="text" id="firstName" placeholder="John" required style="
                                        width: 100%;
                                        height: 48px;
                                        padding: 0 16px;
                                        background: rgba(255, 255, 255, 0.05);
                                        border: 1px solid rgba(255, 255, 255, 0.12);
                                        border-radius: 12px;
                                        color: #ffffff;
                                        font-size: 15px;
                                        transition: all 0.15s ease;
                                    ">
                                </div>
                                
                                <div class="form-group" style="margin-bottom: 24px;">
                                    <label style="
                                        display: block;
                                        font-size: 12px;
                                        font-weight: 600;
                                        color: rgba(255, 255, 255, 0.8);
                                        text-transform: uppercase;
                                        letter-spacing: 0.8px;
                                        margin-bottom: 8px;
                                    ">Last Name <span style="color: #ef4444;">*</span></label>
                                    <input type="text" id="lastName" placeholder="Doe" required style="
                                        width: 100%;
                                        height: 48px;
                                        padding: 0 16px;
                                        background: rgba(255, 255, 255, 0.05);
                                        border: 1px solid rgba(255, 255, 255, 0.12);
                                        border-radius: 12px;
                                        color: #ffffff;
                                        font-size: 15px;
                                        transition: all 0.15s ease;
                                    ">
                                </div>
                            </div>

                            <div class="form-row" style="
                                display: grid;
                                grid-template-columns: 1fr 1fr;
                                gap: 20px;
                            ">
                                <div class="form-group" style="margin-bottom: 24px;">
                                    <label style="
                                        display: block;
                                        font-size: 12px;
                                        font-weight: 600;
                                        color: rgba(255, 255, 255, 0.8);
                                        text-transform: uppercase;
                                        letter-spacing: 0.8px;
                                        margin-bottom: 8px;
                                    ">Password <span style="color: #ef4444;">*</span></label>
                                    <input type="password" id="password" placeholder="Minimum 12 characters" required style="
                                        width: 100%;
                                        height: 48px;
                                        padding: 0 16px;
                                        background: rgba(255, 255, 255, 0.05);
                                        border: 1px solid rgba(255, 255, 255, 0.12);
                                        border-radius: 12px;
                                        color: #ffffff;
                                        font-size: 15px;
                                        transition: all 0.15s ease;
                                    ">
                                </div>
                                
                                <div class="form-group" style="margin-bottom: 24px;">
                                    <label style="
                                        display: block;
                                        font-size: 12px;
                                        font-weight: 600;
                                        color: rgba(255, 255, 255, 0.8);
                                        text-transform: uppercase;
                                        letter-spacing: 0.8px;
                                        margin-bottom: 8px;
                                    ">Confirm Password <span style="color: #ef4444;">*</span></label>
                                    <input type="password" id="confirmPassword" placeholder="Re-enter password" required style="
                                        width: 100%;
                                        height: 48px;
                                        padding: 0 16px;
                                        background: rgba(255, 255, 255, 0.05);
                                        border: 1px solid rgba(255, 255, 255, 0.12);
                                        border-radius: 12px;
                                        color: #ffffff;
                                        font-size: 15px;
                                        transition: all 0.15s ease;
                                    ">
                                </div>
                            </div>
                            
                            <!-- Password Requirements -->
                            <div class="password-requirements" style="
                                display: grid;
                                grid-template-columns: repeat(3, 1fr);
                                gap: 16px;
                                margin-bottom: 32px;
                                padding: 20px;
                                background: rgba(255, 255, 255, 0.02);
                                border: 1px solid rgba(255, 255, 255, 0.05);
                                border-radius: 12px;
                            ">
                                <div class="requirement" id="reqLength" style="
                                    display: flex;
                                    align-items: center;
                                    gap: 8px;
                                    font-size: 12px;
                                    color: rgba(148, 163, 184, 1);
                                ">
                                    <svg class="requirement-icon" style="
                                        width: 16px;
                                        height: 16px;
                                        color: rgba(148, 163, 184, 1);
                                    " fill="currentColor" viewBox="0 0 20 20">
                                        <circle cx="10" cy="10" r="8" fill="none" stroke="currentColor" stroke-width="2"/>
                                    </svg>
                                    <span>At least 12 characters</span>
                                </div>
                                <div class="requirement" id="reqUpper" style="
                                    display: flex;
                                    align-items: center;
                                    gap: 8px;
                                    font-size: 12px;
                                    color: rgba(148, 163, 184, 1);
                                ">
                                    <svg class="requirement-icon" style="
                                        width: 16px;
                                        height: 16px;
                                        color: rgba(148, 163, 184, 1);
                                    " fill="currentColor" viewBox="0 0 20 20">
                                        <circle cx="10" cy="10" r="8" fill="none" stroke="currentColor" stroke-width="2"/>
                                    </svg>
                                    <span>One uppercase letter</span>
                                </div>
                                <div class="requirement" id="reqNumber" style="
                                    display: flex;
                                    align-items: center;
                                    gap: 8px;
                                    font-size: 12px;
                                    color: rgba(148, 163, 184, 1);
                                ">
                                    <svg class="requirement-icon" style="
                                        width: 16px;
                                        height: 16px;
                                        color: rgba(148, 163, 184, 1);
                                    " fill="currentColor" viewBox="0 0 20 20">
                                        <circle cx="10" cy="10" r="8" fill="none" stroke="currentColor" stroke-width="2"/>
                                    </svg>
                                    <span>One number or symbol</span>
                                </div>
                            </div>

                            <!-- Action Buttons -->
                            <div style="
                                display: flex;
                                gap: 16px;
                                justify-content: flex-end;
                                margin-top: 32px;
                            ">
                                <button type="button" id="back-to-signin" style="
                                    padding: 14px 32px;
                                    background: transparent;
                                    border: 2px solid rgba(255, 255, 255, 0.2);
                                    border-radius: 12px;
                                    color: rgba(255, 255, 255, 0.8);
                                    font-size: 15px;
                                    font-weight: 600;
                                    cursor: pointer;
                                    transition: all 0.15s ease;
                                ">Already have account?</button>
                                
                                <button type="submit" style="
                                    padding: 14px 32px;
                                    background: linear-gradient(135deg, #00d4ff 0%, #0ea5e9 100%);
                                    border: none;
                                    border-radius: 12px;
                                    color: #ffffff;
                                    font-size: 15px;
                                    font-weight: 600;
                                    cursor: pointer;
                                    transition: all 0.15s ease;
                                    box-shadow: 0 4px 16px rgba(0, 212, 255, 0.3);
                                ">Create Account</button>
                            </div>
                        </div>
                    </form>
                </div>
            </div>

            <!-- CSS Styling -->
            <style>
                :root {
                    --primary: #00d4ff;
                    --primary-rgb: 0, 212, 255;
                    --primary-dark: #0ea5e9;
                    --background: #0f172a;
                    --glass: rgba(255, 255, 255, 0.02);
                    --border: rgba(255, 255, 255, 0.08);
                    --text-primary: #ffffff;
                    --text-secondary: rgba(255, 255, 255, 0.8);
                    --text-muted: rgba(148, 163, 184, 1);
                    --success: #10b981;
                    --error: #ef4444;
                }

                /* Progress Bar */
                .signup-progress-bar {
                    padding: 24px 48px !important;
                    background: var(--glass) !important;
                    border-bottom: 1px solid var(--border) !important;
                }
                
                .signup-progress-steps {
                    display: flex !important;
                    justify-content: space-between !important;
                    position: relative !important;
                }
                
                .signup-progress-line {
                    position: absolute !important;
                    top: 20px !important;
                    left: 0 !important;
                    right: 0 !important;
                    height: 2px !important;
                    background: var(--border) !important;
                    z-index: 0 !important;
                }
                
                .signup-progress-line-fill {
                    position: absolute !important;
                    top: 0 !important;
                    left: 0 !important;
                    height: 100% !important;
                    background: linear-gradient(90deg, var(--primary) 0%, var(--primary-dark) 100%) !important;
                    transition: width 0.5s cubic-bezier(0.4, 0, 0.2, 1) !important;
                }
                
                .signup-step {
                    position: relative !important;
                    z-index: 1 !important;
                    text-align: center !important;
                    flex: 1 !important;
                }
                
                .signup-step-number {
                    width: 40px !important;
                    height: 40px !important;
                    margin: 0 auto 8px !important;
                    background: var(--background) !important;
                    border: 2px solid var(--border) !important;
                    border-radius: 50% !important;
                    display: flex !important;
                    align-items: center !important;
                    justify-content: center !important;
                    font-size: 14px !important;
                    font-weight: 700 !important;
                    color: var(--text-muted) !important;
                    transition: all 0.15s ease !important;
                }
                
                .signup-step.active .signup-step-number {
                    background: linear-gradient(135deg, var(--primary) 0%, var(--primary-dark) 100%) !important;
                    border-color: var(--primary) !important;
                    color: var(--background) !important;
                    transform: scale(1.1) !important;
                }

                .signup-step-label {
                    font-size: 12px !important;
                    font-weight: 500 !important;
                    color: var(--text-muted) !important;
                }
                
                .signup-step.active .signup-step-label {
                    color: var(--primary) !important;
                }

                @keyframes cardEntry {
                    from {
                        opacity: 0;
                        transform: scale(0.95) translateY(30px);
                    }
                    to {
                        opacity: 1;
                        transform: scale(1) translateY(0);
                    }
                }

                input:hover {
                    background: rgba(255, 255, 255, 0.06) !important;
                }
                
                input:focus {
                    outline: none;
                    background: rgba(255, 255, 255, 0.08) !important;
                    border-color: var(--primary) !important;
                    box-shadow: 0 0 0 3px rgba(var(--primary-rgb), 0.1) !important;
                }

                button[type="submit"]:hover {
                    transform: translateY(-1px);
                    box-shadow: 0 6px 20px rgba(var(--primary-rgb), 0.5) !important;
                }

                #back-to-signin:hover {
                    background: rgba(255, 255, 255, 0.05) !important;
                    border-color: rgba(255, 255, 255, 0.3) !important;
                }

                .close-btn:hover {
                    background: rgba(255, 255, 255, 0.08) !important;
                    transform: rotate(90deg);
                }

                /* Password Requirements Validation */
                .requirement.valid {
                    color: var(--success) !important;
                }

                .requirement.valid .requirement-icon {
                    color: var(--success) !important;
                }
            </style>
        `;

        // Add to body
        document.body.appendChild(overlay);

        // Bind events
        this.bindSignUpEvents();

        // Add password validation
        this.setupPasswordValidation();

        // Focus email input
        setTimeout(() => {
            const emailInput = document.getElementById('corporateEmail');
            if (emailInput) emailInput.focus();
        }, 100);
    }

    createMobileSignUpInterface() {
        // Create mobile-optimized overlay using mobile template design
        const overlay = document.createElement('div');
        overlay.id = this.overlayId;
        overlay.style.cssText = `
            position: fixed;
            top: 0;
            left: 0;
            width: 100vw;
            height: 100vh;
            background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
            color: #ffffff;
            z-index: 10000;
            overflow-y: auto;
            font-family: -apple-system, BlinkMacSystemFont, 'SF Pro Display', 'Segoe UI', system-ui, sans-serif;
        `;

        overlay.innerHTML = `
            <!-- Mobile Container -->
            <div class="signup-container" style="
                display: flex;
                flex-direction: column;
                min-height: 100vh;
                padding: 24px;
                padding-top: calc(24px + env(safe-area-inset-top));
                padding-bottom: calc(24px + env(safe-area-inset-bottom));
            ">
                <!-- Header -->
                <div class="signup-header" style="
                    text-align: center;
                    margin-bottom: 32px;
                ">
                    <button id="mobile-back-to-home" style="
                        position: absolute;
                        left: 24px;
                        top: calc(24px + env(safe-area-inset-top));
                        width: 40px;
                        height: 40px;
                        background: rgba(255, 255, 255, 0.05);
                        border: 1px solid rgba(255, 255, 255, 0.1);
                        border-radius: 12px;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        color: rgba(255, 255, 255, 0.7);
                        cursor: pointer;
                        transition: all 0.15s ease;
                        font-size: 18px;
                    ">←</button>
                    
                    <div style="
                        display: inline-flex;
                        align-items: center;
                        gap: 12px;
                        margin-bottom: 24px;
                    ">
                        <div style="
                            width: 48px;
                            height: 48px;
                            background: linear-gradient(135deg, #00d4ff 0%, #0ea5e9 100%);
                            border-radius: 12px;
                            display: flex;
                            align-items: center;
                            justify-content: center;
                            font-size: 24px;
                            font-weight: 800;
                            color: #0f172a;
                        ">D</div>
                        <div style="
                            font-size: 20px;
                            font-weight: 700;
                            color: #ffffff;
                        ">DeckOps.ai</div>
                    </div>
                    
                    <h1 style="
                        font-size: 24px;
                        font-weight: 600;
                        margin-bottom: 8px;
                        color: #ffffff;
                    ">Create Account</h1>
                    <p style="
                        font-size: 14px;
                        color: rgba(255, 255, 255, 0.7);
                    ">Join the executive platform</p>
                </div>

                <!-- Mobile Form -->
                <form id="mobile-signup-form" style="
                    flex: 1;
                    display: flex;
                    flex-direction: column;
                    gap: 20px;
                ">
                    <div class="form-group">
                        <label style="
                            display: block;
                            font-size: 12px;
                            font-weight: 600;
                            color: rgba(255, 255, 255, 0.8);
                            text-transform: uppercase;
                            letter-spacing: 0.5px;
                            margin-bottom: 8px;
                        ">Full Name <span style="color: #ef4444;">*</span></label>
                        <input type="text" id="mobile-fullName" placeholder="Enter your full name" required style="
                            width: 100%;
                            height: 52px;
                            padding: 0 16px;
                            background: rgba(255, 255, 255, 0.05);
                            border: 1px solid rgba(255, 255, 255, 0.1);
                            border-radius: 12px;
                            color: #ffffff;
                            font-size: 16px;
                            transition: all 0.15s ease;
                        ">
                    </div>

                    <div class="form-group">
                        <label style="
                            display: block;
                            font-size: 12px;
                            font-weight: 600;
                            color: rgba(255, 255, 255, 0.8);
                            text-transform: uppercase;
                            letter-spacing: 0.5px;
                            margin-bottom: 8px;
                        ">Email Address <span style="color: #ef4444;">*</span></label>
                        <input type="email" id="mobile-email" placeholder="your.email@company.com" required style="
                            width: 100%;
                            height: 52px;
                            padding: 0 16px;
                            background: rgba(255, 255, 255, 0.05);
                            border: 1px solid rgba(255, 255, 255, 0.1);
                            border-radius: 12px;
                            color: #ffffff;
                            font-size: 16px;
                            transition: all 0.15s ease;
                        ">
                    </div>

                    <div class="form-group">
                        <label style="
                            display: block;
                            font-size: 12px;
                            font-weight: 600;
                            color: rgba(255, 255, 255, 0.8);
                            text-transform: uppercase;
                            letter-spacing: 0.5px;
                            margin-bottom: 8px;
                        ">Password <span style="color: #ef4444;">*</span></label>
                        <input type="password" id="mobile-password" placeholder="Create a strong password" required style="
                            width: 100%;
                            height: 52px;
                            padding: 0 16px;
                            background: rgba(255, 255, 255, 0.05);
                            border: 1px solid rgba(255, 255, 255, 0.1);
                            border-radius: 12px;
                            color: #ffffff;
                            font-size: 16px;
                            transition: all 0.15s ease;
                        ">
                    </div>

                    <div class="form-group">
                        <label style="
                            display: block;
                            font-size: 12px;
                            font-weight: 600;
                            color: rgba(255, 255, 255, 0.8);
                            text-transform: uppercase;
                            letter-spacing: 0.5px;
                            margin-bottom: 8px;
                        ">Confirm Password <span style="color: #ef4444;">*</span></label>
                        <input type="password" id="mobile-confirmPassword" placeholder="Confirm your password" required style="
                            width: 100%;
                            height: 52px;
                            padding: 0 16px;
                            background: rgba(255, 255, 255, 0.05);
                            border: 1px solid rgba(255, 255, 255, 0.1);
                            border-radius: 12px;
                            color: #ffffff;
                            font-size: 16px;
                            transition: all 0.15s ease;
                        ">
                    </div>

                    <!-- Terms -->
                    <div style="margin: 24px 0;">
                        <label style="
                            display: flex;
                            align-items: flex-start;
                            gap: 12px;
                            cursor: pointer;
                            font-size: 14px;
                            color: rgba(255, 255, 255, 0.8);
                            line-height: 1.4;
                        ">
                            <input type="checkbox" required style="
                                width: 18px;
                                height: 18px;
                                accent-color: #00d4ff;
                                cursor: pointer;
                                margin-top: 2px;
                            ">
                            <span>I agree to the <a href="#" style="color: #00d4ff; text-decoration: none;">Terms</a> and <a href="#" style="color: #00d4ff; text-decoration: none;">Privacy Policy</a></span>
                        </label>
                    </div>

                    <!-- Submit Button -->
                    <button type="submit" style="
                        width: 100%;
                        height: 52px;
                        background: linear-gradient(135deg, #00d4ff 0%, #0ea5e9 100%);
                        border: none;
                        border-radius: 12px;
                        color: #ffffff;
                        font-size: 16px;
                        font-weight: 600;
                        cursor: pointer;
                        transition: all 0.15s ease;
                        box-shadow: 0 4px 16px rgba(0, 212, 255, 0.3);
                        margin-top: 8px;
                    ">Create Account</button>

                    <!-- Sign In Link -->
                    <div style="
                        text-align: center;
                        margin-top: 24px;
                    ">
                        <span style="
                            color: rgba(255, 255, 255, 0.7);
                            font-size: 14px;
                        ">Already have an account? </span>
                        <a href="#" id="mobile-switch-to-signin" style="
                            color: #00d4ff;
                            text-decoration: none;
                            font-size: 14px;
                            font-weight: 600;
                            transition: color 0.15s ease;
                        ">Sign in</a>
                    </div>
                </form>
            </div>

            <!-- Mobile CSS -->
            <style>
                input:focus {
                    outline: none;
                    background: rgba(255, 255, 255, 0.08) !important;
                    border-color: #00d4ff !important;
                    box-shadow: 0 0 0 3px rgba(0, 212, 255, 0.1) !important;
                }
                
                input::placeholder {
                    color: rgba(255, 255, 255, 0.5);
                }

                button[type="submit"]:hover, button[type="submit"]:active {
                    transform: translateY(-1px);
                    box-shadow: 0 6px 20px rgba(0, 212, 255, 0.4) !important;
                }

                #mobile-back-to-home:active {
                    transform: scale(0.95);
                    background: rgba(255, 255, 255, 0.08) !important;
                }

                #mobile-switch-to-signin:hover {
                    color: #0ea5e9 !important;
                }
            </style>
        `;

        // Add to body
        document.body.appendChild(overlay);

        // Bind events
        this.bindMobileSignUpEvents();

        // Focus name input
        setTimeout(() => {
            const nameInput = document.getElementById('mobile-fullName');
            if (nameInput) nameInput.focus();
        }, 100);
    }

    bindSignUpEvents() {
        // Form submission
        const form = document.getElementById('signupForm');
        if (form) {
            form.addEventListener('submit', (e) => {
                e.preventDefault();
                this.handleSignUp();
            });
        }

        // Close button
        const closeBtn = document.getElementById('close-signup');
        if (closeBtn) {
            closeBtn.addEventListener('click', (e) => {
                e.preventDefault();
                this.goBackToHome();
            });
        }

        // Back to sign in
        const backToSignin = document.getElementById('back-to-signin');
        if (backToSignin) {
            backToSignin.addEventListener('click', (e) => {
                e.preventDefault();
                this.switchToSignIn();
            });
        }
    }

    bindMobileSignUpEvents() {
        // Form submission
        const form = document.getElementById('mobile-signup-form');
        if (form) {
            form.addEventListener('submit', (e) => {
                e.preventDefault();
                this.handleMobileSignUp();
            });
        }

        // Back to home
        const backToHome = document.getElementById('mobile-back-to-home');
        if (backToHome) {
            backToHome.addEventListener('click', (e) => {
                e.preventDefault();
                this.goBackToHome();
            });
        }

        // Switch to sign in
        const switchToSignin = document.getElementById('mobile-switch-to-signin');
        if (switchToSignin) {
            switchToSignin.addEventListener('click', (e) => {
                e.preventDefault();
                this.switchToSignIn();
            });
        }
    }

    setupPasswordValidation() {
        // Add password validation for desktop form
        const passwordInput = document.getElementById('password');
        if (passwordInput) {
            passwordInput.addEventListener('input', () => {
                this.validatePassword();
            });
        }
    }

    validatePassword() {
        const password = document.getElementById('password').value;
        
        // Length requirement
        const lengthReq = document.getElementById('reqLength');
        if (lengthReq) {
            if (password.length >= 12) {
                lengthReq.classList.add('valid');
            } else {
                lengthReq.classList.remove('valid');
            }
        }

        // Uppercase requirement
        const upperReq = document.getElementById('reqUpper');
        if (upperReq) {
            if (/[A-Z]/.test(password)) {
                upperReq.classList.add('valid');
            } else {
                upperReq.classList.remove('valid');
            }
        }

        // Number/symbol requirement
        const numberReq = document.getElementById('reqNumber');
        if (numberReq) {
            if (/[0-9!@#$%^&*(),.?":{}|<>]/.test(password)) {
                numberReq.classList.add('valid');
            } else {
                numberReq.classList.remove('valid');
            }
        }
    }

    async handleSignUp() {
        const firstName = document.getElementById('firstName').value;
        const lastName = document.getElementById('lastName').value;
        const email = document.getElementById('corporateEmail').value;
        const password = document.getElementById('password').value;
        const confirmPassword = document.getElementById('confirmPassword').value;

        if (!firstName || !lastName || !email || !password || !confirmPassword) {
            this.showMessage('Please fill in all required fields', 3000, true);
            return;
        }

        if (password !== confirmPassword) {
            this.showMessage('Passwords do not match', 3000, true);
            return;
        }

        if (password.length < 12) {
            this.showMessage('Password must be at least 12 characters', 3000, true);
            return;
        }

        try {
            // CRITICAL: Use the exact same Firebase auth approach as working system
            const { auth } = await import('../firebaseConfig.js');
            console.log('🔐 Desktop: Attempting Firebase sign up with:', email);
            
            const userCredential = await auth.createUserWithEmailAndPassword(email, password);
            console.log('✅ Desktop: User signed up successfully:', userCredential.user.email);
            
            // Update profile
            await userCredential.user.updateProfile({
                displayName: `${firstName} ${lastName}`
            });
            
            // CRITICAL: Remove overlay immediately to let Firebase auth state change handle routing
            this.removeExistingOverlay();
            console.log('✅ Desktop sign up successful, overlay removed, Firebase will handle routing');
        } catch (error) {
            console.error('Desktop sign up error:', error);
            this.showMessage('Sign up failed. Please try again.', 3000, true);
        }
    }

    async handleMobileSignUp() {
        const fullName = document.getElementById('mobile-fullName').value;
        const email = document.getElementById('mobile-email').value;
        const password = document.getElementById('mobile-password').value;
        const confirmPassword = document.getElementById('mobile-confirmPassword').value;

        if (!fullName || !email || !password || !confirmPassword) {
            this.showMessage('Please fill in all required fields', 3000, true);
            return;
        }

        if (password !== confirmPassword) {
            this.showMessage('Passwords do not match', 3000, true);
            return;
        }

        if (password.length < 6) {
            this.showMessage('Password must be at least 6 characters', 3000, true);
            return;
        }

        try {
            // CRITICAL: Use the exact same Firebase auth approach as working system
            const { auth } = await import('../firebaseConfig.js');
            console.log('🔐 Mobile: Attempting Firebase sign up with:', email);
            
            const userCredential = await auth.createUserWithEmailAndPassword(email, password);
            console.log('✅ Mobile: User signed up successfully:', userCredential.user.email);
            
            // Update profile
            await userCredential.user.updateProfile({
                displayName: fullName
            });
            
            // CRITICAL: Remove overlay immediately to let Firebase auth state change handle routing
            this.removeExistingOverlay();
            console.log('✅ Mobile sign up successful, overlay removed, Firebase will handle routing');
        } catch (error) {
            console.error('Mobile sign up error:', error);
            this.showMessage('Sign up failed. Please try again.', 3000, true);
        }
    }

    switchToSignIn() {
        // Use proper scene transition following project architecture
        this.startScene('SignInSceneDOM');
    }

    goBackToHome() {
        // Use proper scene transition following project architecture
        this.startScene('GuestMenuSceneDOM');
    }

    showMessage(message, duration = 3000, isError = false) {
        // Create temporary message overlay
        const messageDiv = document.createElement('div');
        messageDiv.style.cssText = `
            position: fixed;
            top: 20px;
            right: 20px;
            background: ${isError ? '#ef4444' : '#10b981'};
            color: white;
            padding: 12px 24px;
            border-radius: 8px;
            font-size: 14px;
            font-weight: 500;
            z-index: 10001;
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
        `;
        messageDiv.textContent = message;
        
        document.body.appendChild(messageDiv);
        
        setTimeout(() => {
            if (messageDiv.parentNode) {
                messageDiv.parentNode.removeChild(messageDiv);
            }
        }, duration);
    }

    removeExistingOverlay() {
        const existing = document.getElementById(this.overlayId);
        if (existing) {
            existing.remove();
        }
    }

    destroy() {
        this.removeExistingOverlay();
        super.destroy();
    }
}